import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@overdose/components'
import {
  IconAdjustmentsHorizontal,
  IconSearch,
  IconX,
} from '@tabler/icons-react'
import classNames from 'classnames'
import debounce from 'lodash-es/debounce'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { SiteCodes } from 'shared-types'
import { FilterMapProps } from '~/components/FiltersDrawer'
import Input from '~/components/Input'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  useAlgoliaSearchInsignts,
  useFormatPrice,
  useToast,
  useUserPreferredStore,
} from '~/hooks'
import {
  FilterProvider,
  useFilterContext,
} from '~/providers/FilterProvider/FilterProvider'
import { Pill } from '../Pill'
import { FilterToggleButton } from './FilterToggleButton'
import styles from './ProductListFilters.module.css'
import {
  ProductListFiltersCoreProps,
  ProductListFiltersProps,
} from './ProductListFilters.types'

enum StockFilterId {
  InStoreStock = 'inStoreStock',
  ExcludeEndlessAisle = 'excludeEa',
}

const EA_EXCLUSION_FILTER_VALUE = '1'
const IN_STORE_STOCK_FILTER_VALUE = '1'

const FilterItem = dynamic(
  async () => {
    const mod = await import('./FilterItem')
    return mod.default
  },
  {
    loading: () => {
      return (
        <Pill
          title=''
          className={classNames(styles.filterLoader, 'md: w-24')}
        />
      )
    },
  }
)

const FiltersDrawer = dynamic(
  async () => {
    const mod = await import('~/components/FiltersDrawer')
    return mod.default
  },
  { ssr: false }
)

export const ProductListFilters: FC<ProductListFiltersCoreProps> = ({
  sort: sortOptions,
  search,
  title,
  setPagination,
  siteCode = SiteCodes.JaycarAu,
  searchPlaceholder = 'Search for an article',
  isCategoryPage = false,
  isSearchPage = false,
}) => {
  const router = useRouter()
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)
  const { showNotification, closeNotification } = useToast()
  const { sendFilterEvent } = useAlgoliaSearchInsignts()
  const { promptUserToSelectStore, store } = useUserPreferredStore()
  const [isSelectingStoreForStockFilter, setIsSelectingStoreForStockFilter] =
    useState<boolean>(false)
  const isPlp = isSearchPage || isCategoryPage

  const {
    filters,
    selectedFilterMap,
    setSelectedFilterMap,
    selectedSortOption,
    setSelectedSortOption,
    searchKey,
    setSearchKey,
  } = useFilterContext()

  const canShowEndlessAisleFilter =
    siteCode !== SiteCodes.JaycarNz &&
    selectedFilterMap['productLifeCycleTags.lifeCycleAttribute'] == null

  const hasAllFilters = useMemo(() => {
    const otherFilters = { ...selectedFilterMap }

    delete otherFilters.q
    delete otherFilters[StockFilterId.ExcludeEndlessAisle]
    delete otherFilters[StockFilterId.InStoreStock]

    return Object.values(otherFilters).some((value) => {
      return value?.length > 0 && value[0] !== ''
    })
  }, [selectedFilterMap])

  const canClearFilters = useMemo(() => {
    const selectedFilters = { ...selectedFilterMap }
    const ignoreSearchQuery = !isCategoryPage || isSearchPage

    if (ignoreSearchQuery) {
      delete selectedFilters.q
    }

    return Object.values(selectedFilters).some((value) => {
      return value?.length > 0 && value[0] !== ''
    })
  }, [selectedFilterMap, isCategoryPage, isSearchPage])

  const { formatPrice } = useFormatPrice()

  const handleShowFilterDrawer = useCallback(() => {
    setShowFilterDrawer(true)
  }, [setShowFilterDrawer])

  const handleHideFilterDrawer = useCallback(() => {
    setShowFilterDrawer(false)
  }, [setShowFilterDrawer])

  const updateFilterAndSort = useCallback(
    (selectedFilterMap, selectedSortOption?: string) => {
      if (!selectedFilterMap && !selectedSortOption) {
        return
      }

      const pathname = router?.asPath?.split('?')?.[0]
      const filterUrlParams = {}
      const initialMinPrice = filters
        ?.find((filter) => {
          return filter.uid === 'price'
        })
        ?.options.find((option) => {
          return option.uid === 'minPrice'
        })?.value
      const formattedMinPrice =
        typeof initialMinPrice === 'string'
          ? Number(initialMinPrice)
          : Number(formatPrice(initialMinPrice).slice(1))

      const minPrice =
        Number(selectedFilterMap.minPrice?.[0]) || formattedMinPrice
      for (const key in selectedFilterMap) {
        if (key !== 'sort' && selectedFilterMap[key]?.length > 0) {
          if (
            key === 'maxPrice' &&
            Number(selectedFilterMap.maxPrice?.[0]) < minPrice
          ) {
            showNotification({
              id: 'invalid-value',
              children: 'Max value should be greater than min value',
              type: 'error',
              onClose: closeNotification,
            })
            return
          }
          filterUrlParams[key] = selectedFilterMap[key]?.join('|')
        }
      }

      sendFilterEvent(selectedFilterMap, 'click')

      if (setPagination) {
        setPagination((prev) => {
          return {
            ...prev,
            currentPage: 1,
          }
        })
      }

      router.push(
        {
          pathname,
          query: {
            ...(searchKey ? { q: searchKey } : {}),
            ...filterUrlParams,
            ...(selectedSortOption ? { sort: selectedSortOption } : {}),
          },
        },
        undefined,
        { scroll: false }
      )
    },
    [
      router,
      filters,
      formatPrice,
      sendFilterEvent,
      setPagination,
      searchKey,
      showNotification,
      closeNotification,
    ]
  )

  const handleSortBy = useCallback(
    (_uid, value, checked) => {
      if (checked) {
        setSelectedSortOption(value)
        updateFilterAndSort(selectedFilterMap, value)
      }
    },
    [setSelectedSortOption, updateFilterAndSort, selectedFilterMap]
  )

  const handleFilter = useCallback(
    (uid: string, optionUid: string, checked: boolean) => {
      let updatedSelectedFilterMap = {}
      const currentOptions = selectedFilterMap[uid] || []
      const isPriceFilter = ['minPrice', 'maxPrice'].includes(uid)

      if (checked || (isPriceFilter && optionUid !== '')) {
        updatedSelectedFilterMap = {
          ...selectedFilterMap,
          [uid]: isPriceFilter ? [optionUid] : currentOptions.concat(optionUid),
        }
      } else {
        updatedSelectedFilterMap = {
          ...selectedFilterMap,

          [uid]: currentOptions.filter((el) => {
            return el !== optionUid
          }),
        }
      }

      if (updatedSelectedFilterMap[uid]?.length === 0) {
        delete updatedSelectedFilterMap[uid]
      }
      setSelectedFilterMap(updatedSelectedFilterMap)
      updateFilterAndSort(updatedSelectedFilterMap, selectedSortOption)
    },
    [
      selectedFilterMap,
      setSelectedFilterMap,
      selectedSortOption,
      updateFilterAndSort,
    ]
  )

  useEffect(() => {
    if (!store?.storeKey || !isSelectingStoreForStockFilter) {
      return
    }

    if (isSelectingStoreForStockFilter) {
      handleFilter(
        StockFilterId.InStoreStock,
        IN_STORE_STOCK_FILTER_VALUE,
        true
      )
      setIsSelectingStoreForStockFilter(false)
    }
  }, [store?.storeKey, isSelectingStoreForStockFilter, handleFilter])

  const SEARCH_KEYS_THREE = 3
  const DEBOUNCE_DELAY_HALF_SECOND = 500
  const DEBOUNCE_DELAY_TWO_SECONDS = 2000
  const DEBOUNCE_DELAY_200 = 200

  const handleSearch = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const hasRelevanceSort = sortOptions.some((option) => {
        return option.value === 'relevance'
      })

      if (
        hasRelevanceSort &&
        ((searchKey.length > 0 && e.key === 'Backspace') || e.key === 'Enter')
      ) {
        setSelectedSortOption('relevance')
      }

      if (e.key === 'Enter') {
        updateFilterAndSort(selectedFilterMap, selectedSortOption)
      } else {
        if (searchKey.length > SEARCH_KEYS_THREE) {
          debounce((selectedFilterMap, selectedSortOption) => {
            updateFilterAndSort(selectedFilterMap, selectedSortOption)
          }, DEBOUNCE_DELAY_HALF_SECOND)(selectedFilterMap, selectedSortOption)
        }

        if (searchKey.length > 0 && e.key === 'Backspace') {
          debounce((selectedFilterMap, selectedSortOption) => {
            updateFilterAndSort(selectedFilterMap, selectedSortOption)
          }, DEBOUNCE_DELAY_TWO_SECONDS)(selectedFilterMap, selectedSortOption)
        }

        if (searchKey.length === 0 && e.key === 'Backspace') {
          debounce((selectedFilterMap, selectedSortOption) => {
            updateFilterAndSort(selectedFilterMap, selectedSortOption)
          }, DEBOUNCE_DELAY_200)(selectedFilterMap, selectedSortOption)
          setSelectedSortOption(String(sortOptions[0].value))
        }
      }
    },
    [
      sortOptions,
      searchKey,
      setSelectedSortOption,
      updateFilterAndSort,
      selectedFilterMap,
      selectedSortOption,
    ]
  )

  const handleResetSelectedFilterMap = useCallback(
    (filterMap: FilterMapProps) => {
      setShowFilterDrawer(false)
      setSelectedFilterMap(filterMap)
      updateFilterAndSort(filterMap, selectedSortOption)
    },
    [selectedSortOption, setSelectedFilterMap, updateFilterAndSort]
  )

  const clearFilters = () => {
    /* 
      In the category PLP, the search query must also be removed when clearing filters.
      In the search PLP, the search query is always preserved (so the user doesn't go back to "All Products")
      In any other page, the search query is preserved when clearing filters.
    */
    if (isCategoryPage && !isSearchPage) {
      handleResetSelectedFilterMap({})
    } else {
      const currentFilter = {
        ...(selectedFilterMap?.q && { q: selectedFilterMap.q }),
      }
      handleResetSelectedFilterMap(currentFilter)
    }
  }

  const renderStockFilters = useCallback(() => {
    const inStoreStockFilterLabel = store?.branchCode ? (
      `Show Products In-Stock at ${store.displayName}`
    ) : (
      <>
        Show Products In-Stock at{' '}
        <span className='!underline'>Set Store Here</span>
      </>
    )

    return (
      <div className='flex flex-col flex-grow items-center gap-y-2 gap-x-2 xl:gap-x-4 sm:flex-row'>
        <FilterToggleButton
          label={inStoreStockFilterLabel}
          buttonClassName='w-full sm:w-auto'
          selectedFilters={selectedFilterMap}
          filterId={StockFilterId.InStoreStock}
          onClick={(filterId) => {
            const filterValue = selectedFilterMap[filterId]?.[0]

            if (filterValue) {
              return handleFilter(filterId, filterValue, false)
            }

            if (store) {
              return handleFilter(filterId, IN_STORE_STOCK_FILTER_VALUE, true)
            }

            setIsSelectingStoreForStockFilter(true)
            promptUserToSelectStore(undefined, false)
          }}
        />
        {canShowEndlessAisleFilter && (
          <FilterToggleButton
            label='Exclude Marketplace Products'
            buttonClassName='w-full sm:w-auto'
            selectedFilters={selectedFilterMap}
            filterId={StockFilterId.ExcludeEndlessAisle}
            expectedFilterValue={EA_EXCLUSION_FILTER_VALUE}
            onClick={(filterId) => {
              handleFilter(
                filterId,
                EA_EXCLUSION_FILTER_VALUE,
                !selectedFilterMap[filterId]
              )
            }}
          />
        )}
      </div>
    )
  }, [
    store,
    selectedFilterMap,
    canShowEndlessAisleFilter,
    promptUserToSelectStore,
    handleFilter,
  ])

  return (
    <div
      className={classNames(
        styles.productListFilters,
        'flex w-full gap-2',
        `justify-between lg:justify-${search ? 'between' : 'start'}`,
        {
          'sm:flex-row': sortOptions?.length > 0,
          'xs:flex-row': !sortOptions?.length,
          'flex-col sm:flex-row': !isPlp,
          'flex-col sm:flex-row flex-wrap lg:flex-nowrap': isPlp,
        }
      )}>
      <div
        className={classNames(
          'flex-wrap',
          'flex gap-x-2 xl:gap-x-4 gap-y-4 items-center',
          'justify-end sm:justify-start',
          {
            'flex-grow': !isPlp,
          }
        )}>
        {title && (
          <Typography
            tag={TypographyTag.span}
            className='hidden lg:block'
            variant={TypographyVariant.BodyLargeBold}>
            {title}
          </Typography>
        )}
        {sortOptions?.length > 0 && !search && (
          <>
            <FilterItem
              uid='sort'
              suffix='Sort By'
              selectionType='single'
              options={sortOptions}
              selected={{ sort: [selectedSortOption] }}
              onSelect={handleSortBy}
              buttonClassName='flex-grow sm:flex-grow-0'
              suffixClassName='hidden 2sm:inline'
            />
            <div className={classNames(styles.divider, 'hidden lg:block')} />
          </>
        )}

        {filters?.map((filter, index) => {
          return filter.isFeatured ? (
            <div
              key={index}
              className={`${
                sortOptions?.length > 0 ? 'hidden' : 'block '
              } lg:block`}>
              <div key={index} className='hidden lg:block'>
                <FilterItem
                  {...filter}
                  selected={selectedFilterMap}
                  onSelect={handleFilter}
                />
              </div>
            </div>
          ) : null
        })}

        {isPlp && <div className='hidden lg:block'>{renderStockFilters()}</div>}

        {!search && sortOptions?.length > 0 && (
          <div className='flex justify-end'>
            <Button
              status={hasAllFilters ? 'primary' : 'secondary'}
              shape='rounded'
              theme={{
                root: styles.filterBtn,
              }}
              icon={<IconAdjustmentsHorizontal />}
              iconAlignment='left'
              onClick={handleShowFilterDrawer}>
              <Typography
                tag={TypographyTag.span}
                variant={TypographyVariant.BodySmallBold}
                className={
                  hasAllFilters
                    ? 'text-secondary-heading'
                    : 'text-primary-heading'
                }>
                All Filters
              </Typography>
            </Button>
          </div>
        )}
        {canClearFilters && (
          <Button
            status='secondary'
            shape='rounded'
            theme={{
              root: styles.filterBtn,
            }}
            icon={<IconX size={16} />}
            iconAlignment='left'
            onClick={clearFilters}>
            <Typography
              tag={TypographyTag.span}
              variant={TypographyVariant.BodySmallBold}
              className='text-primary-heading'>
              Clear Filters
            </Typography>
          </Button>
        )}
      </div>
      {search && (
        <div
          className={classNames(
            'flex flex-col gap-2 xl:gap-x-4 max-lg:w-full',
            {
              '2sm:flex-row': sortOptions?.length > 0,
              'xs:flex-row': !sortOptions?.length,
            }
          )}>
          <div className='bg-white rounded-md flex-grow'>
            <Input
              name='search'
              variant='default'
              placeholder={searchPlaceholder}
              prefix={<IconSearch size={20} className={styles.stroke2} />}
              value={searchKey}
              wrapperStyles={styles.searchInputFieldWrapper}
              onChange={setSearchKey}
              onKeyDown={handleSearch}
            />
          </div>
          {sortOptions?.length > 0 && (
            <div className={classNames(styles.divider, 'hidden lg:block')} />
          )}
          <div
            className={classNames(
              'flex items-center gap-2',
              'justify-end sm:justify-between',
              'flex-wrap xs:flex-nowrap'
            )}>
            <div className='lg:hidden'>
              <Button
                status='secondary'
                shape='rounded'
                theme={{
                  root: styles.filterBtn,
                }}
                icon={
                  <IconAdjustmentsHorizontal className='hidden 2sm:block' />
                }
                iconAlignment='left'
                onClick={handleShowFilterDrawer}>
                <Typography
                  tag={TypographyTag.span}
                  variant={TypographyVariant.BodySmallBold}
                  className='text-primary-heading'>
                  All Filters
                </Typography>
              </Button>
            </div>
            {sortOptions?.length > 0 && (
              <FilterItem
                uid='sort'
                suffix='Sort By'
                selectionType='single'
                options={sortOptions}
                selected={{ sort: [selectedSortOption] }}
                onSelect={handleSortBy}
              />
            )}
          </div>
        </div>
      )}
      {isPlp && (
        <div className='flex flex-grow lg:hidden lg:flex-grow-0'>
          {renderStockFilters()}
        </div>
      )}
      <FiltersDrawer
        open={showFilterDrawer}
        filters={filters}
        selected={selectedFilterMap}
        onClose={handleHideFilterDrawer}
        onChange={handleResetSelectedFilterMap}
        isCategoryPage={isCategoryPage}
        isSearchPage={isSearchPage}
      />
    </div>
  )
}

const ProductListFiltersWrapper: FC<ProductListFiltersProps> = (props) => {
  const { selectedFilters, filters, selectedSort, ..._props } = props

  return (
    <FilterProvider
      filters={filters}
      selectedFilters={selectedFilters}
      selectedSort={selectedSort}>
      <ProductListFilters {..._props} />
    </FilterProvider>
  )
}

export default ProductListFiltersWrapper
